import { useMemo, useState } from 'react'

import {
  ActionIcon,
  Collapse,
  NumberInput,
  Select,
  Text,
  Tooltip,
} from '@mantine/core'
import { DateInput } from '@mantine/dates'
import dayjs from 'dayjs'
import {
  ChevronDown,
  ChevronRight,
  Mail,
  MessageSquare,
  RefreshCw,
  X,
} from 'lucide-react'

import { useAuth } from 'src/auth'
import StyledBadge from 'src/components/DataDisplay/StyledBadge/StyledBadge'
import {
  ExpirationNotificationStatus,
  ExpirationNotificationStatusColor,
} from 'src/graphql/types/expirationTrackers'
import { isAdmin } from 'src/lib/auth/permissions'
import { formatSnakeValue } from 'src/lib/formatters'

import { formatNotificationError } from './formatNotificationError'

const ACTION_TYPE_LABEL: Record<string, string> = {
  SMS: 'SMS',
  EMAIL: 'Email',
}

const RECIPIENT_LABEL: Record<string, string> = {
  APPLICANT: 'Applicant',
  EMPLOYEE: 'Employee',
}

const NotificationsSection = ({
  notifications,
  onCancelNotification,
  onRescheduleNotification,
  onAddNotification,
  onSyncNotifications = undefined,
  trackerExpirationDate,
}) => {
  const { currentUser } = useAuth()
  const canSync = isAdmin(currentUser) && Boolean(onSyncNotifications)
  const [isExpanded, setIsExpanded] = useState(true)
  const [showAddForm, setShowAddForm] = useState(false)
  const [newDate, setNewDate] = useState<Date | null>(null)
  const [newOffsetDays, setNewOffsetDays] = useState<number | ''>('')
  const [newActionType, setNewActionType] = useState<string>('SMS')
  const [newRecipient, setNewRecipient] = useState<string>('APPLICANT')

  const handleDateChange = (date: Date | null) => {
    setNewDate(date)
    if (date && trackerExpirationDate) {
      const offset = dayjs(trackerExpirationDate)
        .startOf('day')
        .diff(dayjs(date).startOf('day'), 'day')
      setNewOffsetDays(Math.max(offset, 0))
    }
  }

  const handleOffsetChange = (value: number | '') => {
    setNewOffsetDays(value)
    if (typeof value === 'number' && trackerExpirationDate) {
      const date = dayjs(trackerExpirationDate)
        .subtract(value, 'day')
        .toDate()
      setNewDate(date)
    }
  }

  const isDateInPast =
    newDate && dayjs(newDate).startOf('day').isBefore(dayjs().startOf('day'))

  // TODO: Re-enable when manual notification addition sends Inngest execute events.
  const handleAdd = () => {
    // if (!newDate || !trackerExpirationDate || isDateInPast) return
    // const offsetDays =
    //   typeof newOffsetDays === 'number' ? newOffsetDays : 0
    //
    // onAddNotification({
    //   offsetDays,
    //   scheduledDate: newDate.toISOString(),
    //   actionType: newActionType,
    //   recipient: newRecipient,
    // })
    // setShowAddForm(false)
    // setNewDate(null)
    // setNewOffsetDays('')
  }


  const sortedNotifications = useMemo(() => {
    if (!notifications) return []
    return [...notifications]
      .sort((a, b) => a.offsetDays - b.offsetDays)
  }, [notifications])

  const renderNotification = (notification) => {
    const days = notification.offsetDays
    const daysLabel = `${days} ${days === 1 ? 'day' : 'days'} before`

    return (
      <div
        key={notification.expirationNotificationId}
        className="flex items-center justify-between gap-2 rounded border border-doubleNickel-gray-200 px-3 py-2"
      >
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <Tooltip
              label={ACTION_TYPE_LABEL[notification.actionType] ?? notification.actionType}
              position="top"
              openDelay={300}
            >
              {notification.actionType === 'SMS' ? (
                <MessageSquare size={14} strokeWidth={1.5} className="shrink-0 text-doubleNickel-gray-400" />
              ) : (
                <Mail size={14} strokeWidth={1.5} className="shrink-0 text-doubleNickel-gray-400" />
              )}
            </Tooltip>
            <Text size="xs" fw={400}>
              {daysLabel}
              {notification.recipient && (
                <span className="text-doubleNickel-gray-400">
                  {' '}
                  &middot; {RECIPIENT_LABEL[notification.recipient] ?? notification.recipient}
                </span>
              )}
            </Text>
          </div>
          {notification.failedAt ? (
            <Tooltip
              label={formatNotificationError(notification.errorMessage)}
              position="top"
              multiline
              maw={360}
              openDelay={300}
            >
              <Text
                size="xs"
                lineClamp={2}
                className="text-doubleNickel-gray-500"
              >
                {`Failed ${dayjs(notification.failedAt).format('MM/DD/YYYY')} \u00B7 ${formatNotificationError(notification.errorMessage)}`}
              </Text>
            </Tooltip>
          ) : (
            <Text size="xs" className="text-doubleNickel-gray-500">
              {notification.sentAt
                ? `Sent ${dayjs(notification.sentAt).format('MM/DD/YYYY [at] h:mm A')}`
                : notification.cancelledAt
                  ? `Cancelled ${dayjs(notification.cancelledAt).format('MM/DD/YYYY')}`
                  : dayjs(notification.scheduledDate).format('MM/DD/YYYY')}
            </Text>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <StyledBadge
            textTransform="capitalize"
            color={
              ExpirationNotificationStatusColor[notification.status] || 'gray'
            }
          >
            {formatSnakeValue(notification.status)}
          </StyledBadge>
          {notification.status === ExpirationNotificationStatus.SCHEDULED && (
            <Tooltip label="Cancel">
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label="Cancel"
                onClick={() =>
                  onCancelNotification(notification.expirationNotificationId)
                }
              >
                <X size={12} className="stroke-doubleNickel-gray-500" />
              </ActionIcon>
            </Tooltip>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <button
          className="flex items-center gap-1 text-doubleNickel-gray-500 hover:text-doubleNickel-gray-700"
          onClick={() => setIsExpanded((v) => !v)}
        >
          {isExpanded ? (
            <ChevronDown size={12} />
          ) : (
            <ChevronRight size={12} />
          )}
          <Text size="xs" fw={400} className="uppercase text-inherit">
            SCHEDULED ACTIONS
            <span className="ml-1 text-doubleNickel-gray-400">
              ({sortedNotifications.length})
            </span>
          </Text>
        </button>

        {canSync && (
          <Tooltip
            label="Admin only - Only adds notifications that are missing from the current schedule"
            position="top"
            openDelay={300}
            multiline
            maw={260}
          >
            <button
              type="button"
              onClick={() => onSyncNotifications?.()}
              className="flex items-center gap-1 text-xs text-doubleNickel-gray-500 hover:text-doubleNickel-gray-700"
            >
              <span className="relative flex">
                <RefreshCw size={12} />
                <span className="absolute -right-1 -top-1 h-1 w-1 rounded-full bg-doubleNickel-error-400" />
              </span>
              Sync with config
            </button>
          </Tooltip>
        )}
      </div>

      <Collapse in={isExpanded}>
        {showAddForm && (
          <div className="flex flex-col gap-2 rounded border border-doubleNickel-brand-200 bg-doubleNickel-brand-25 p-3">
            <div className="flex gap-2">
              <DateInput
                label="Scheduled Date"
                placeholder="Select date"
                size="xs"
                value={newDate}
                onChange={handleDateChange}
                minDate={new Date()}
                className="flex-1"
              />
              <NumberInput
                label="Days before exp."
                placeholder="e.g. 30"
                size="xs"
                min={0}
                value={newOffsetDays}
                onChange={handleOffsetChange}
                className="w-[110px]"
              />
            </div>
            <div className="flex gap-2">
              <Select
                label="Type"
                size="xs"
                data={[
                  { value: 'SMS', label: 'SMS' },
                  { value: 'EMAIL', label: 'Email' },
                ]}
                value={newActionType}
                onChange={setNewActionType}
                className="flex-1"
              />
              <Select
                label="Recipient"
                size="xs"
                data={[
                  { value: 'APPLICANT', label: 'Applicant' },
                  { value: 'EMPLOYEE', label: 'Employee' },
                ]}
                value={newRecipient}
                onChange={setNewRecipient}
                className="flex-1"
              />
            </div>
            {isDateInPast && (
              <span className="text-xs text-doubleNickel-error-500">
                Scheduled date cannot be in the past
              </span>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button
                className="text-xs font-medium text-doubleNickel-brand-500 hover:text-doubleNickel-brand-600 disabled:opacity-40"
                onClick={handleAdd}
                disabled={!newDate || isDateInPast}
              >
                Add Notification
              </button>
              <button
                className="text-xs text-doubleNickel-error-500 hover:text-doubleNickel-error-600"
                onClick={() => {
                  setShowAddForm(false)
                  setNewDate(null)
                  setNewOffsetDays('')
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {notifications?.length > 0 ? (
          <div className="flex flex-col gap-2 pt-1">
            {sortedNotifications.map(renderNotification)}
          </div>
        ) : (
          !showAddForm && (
            <Text size="xs" className="text-doubleNickel-gray-400">
              No scheduled actions
            </Text>
          )
        )}
      </Collapse>
    </div>
  )
}

export default NotificationsSection
